import { z } from 'zod';

import { entityIdSchema, nonEmptyTextSchema, sha256Schema } from './primitives';
import { inspectionKpiPolicyIdSchema } from './inspection-finding';

export const inspectionSetupRequestSchema = z
  .object({
    tenantId: z.literal('kop'),
    siteId: entityIdSchema,
  })
  .strict();

export const inspectionSetupContractorSchema = z
  .object({
    contractorId: entityIdSchema,
    label: nonEmptyTextSchema.max(200),
    engagementId: entityIdSchema,
  })
  .strict();

export const inspectionSetupWorkLocationSchema = z
  .object({
    workLocationId: entityIdSchema,
    tradeId: entityIdSchema,
    label: nonEmptyTextSchema.max(300),
    version: z.number().int().positive(),
    eligibleContractors: z.array(inspectionSetupContractorSchema),
  })
  .strict();

export const inspectionSetupTradeSchema = z
  .object({
    tradeId: entityIdSchema,
    label: nonEmptyTextSchema.max(300),
    version: z.number().int().positive(),
    workLocations: z.array(inspectionSetupWorkLocationSchema).min(1),
  })
  .strict();

export const inspectionSetupAreaSchema = z
  .object({
    areaId: entityIdSchema,
    label: nonEmptyTextSchema.max(200),
    contractors: z.array(inspectionSetupContractorSchema),
  })
  .strict();

export const inspectionSetupCategorySchema = z
  .object({
    categoryId: entityIdSchema,
    label: nonEmptyTextSchema.max(200),
    injuryTypeCodes: z.array(nonEmptyTextSchema.max(100)),
    sortOrder: z.number().int().nonnegative(),
  })
  .strict();

export const inspectionSetupTemplateCategorySchema = z
  .object({
    categoryId: entityIdSchema,
    label: nonEmptyTextSchema.max(200),
    items: z.array(
      z
        .object({
          itemId: entityIdSchema,
          label: nonEmptyTextSchema.max(2_000),
        })
        .strict(),
    ),
  })
  .strict();

export const inspectionSetupTemplateSchema = z
  .object({
    templateId: entityIdSchema,
    name: nonEmptyTextSchema.max(300),
    kind: z.enum(['structured', 'special']),
    templateVersion: z.number().int().positive(),
    templateHash: sha256Schema,
    categories: z.array(inspectionSetupTemplateCategorySchema).min(1),
  })
  .strict();

export const inspectionSetupParticipantSchema = z
  .object({
    userId: entityIdSchema,
    name: nonEmptyTextSchema.max(100),
    organizationId: entityIdSchema,
    organization: nonEmptyTextSchema.max(200),
    title: z.string().trim().max(100),
    contractorId: entityIdSchema.nullable(),
  })
  .strict();

export const inspectionSetupMissingAxisSchema = z.enum([
  'area',
  'trade',
  'work-location',
  'category',
  'structured-template',
  'special-template',
  'contractor',
]);

const inspectionSetupModeReadinessSchema = z
  .object({
    ready: z.boolean(),
    missingAxes: z.array(inspectionSetupMissingAxisSchema),
  })
  .strict();

export const inspectionSetupReadinessSchema = z
  .object({
    quick: inspectionSetupModeReadinessSchema,
    structured: inspectionSetupModeReadinessSchema,
    special: inspectionSetupModeReadinessSchema,
    findingAssignment: inspectionSetupModeReadinessSchema,
  })
  .strict();

export const inspectionSetupDuePolicySchema = z.union([
  z.object({ ready: z.literal(true), source: z.enum(['default', 'configured']), version: z.number().int().nonnegative(),
    policyRevision: nonEmptyTextSchema, days: z.number().int().min(1).max(365), calendar: z.literal('calendar'),
    defaultDueOn: z.iso.date(), canManage: z.boolean() }).strict(),
  z
    .object({
      ready: z.literal(true),
      source: z.literal('legacy'),
      version: z.literal(0),
      policyRevision: nonEmptyTextSchema,
      businessDays: z.literal(3),
      saturdayIncluded: z.literal(true),
      sundayExcluded: z.literal(true),
      holidaysCompleteThrough: z.iso.date(),
      defaultDueOn: z.iso.date(),
      canManage: z.boolean(),
    })
    .strict(),
  z
    .object({
      ready: z.literal(false),
      source: z.literal('invalid'),
      version: z.literal(0),
      policyRevision: nonEmptyTextSchema,
      reason: z.literal('BUSINESS_CALENDAR_INCOMPLETE'),
      canManage: z.boolean(),
    })
    .strict(),
]);

export const inspectionSetupOptionsSchema = z
  .object({
    tenantId: z.literal('kop'),
    siteId: entityIdSchema,
    actor: z
      .object({
        identityId: entityIdSchema,
        displayName: nonEmptyTextSchema.max(100),
        jobTitle: z.string().trim().max(100),
        organizationId: entityIdSchema,
        organizationName: nonEmptyTextSchema.max(200),
        organizationType: z.enum(['site', 'contractor']),
        contractorId: entityIdSchema.nullable(),
        authorKind: z.enum(['internal', 'partner']),
        kpiPolicyId: inspectionKpiPolicyIdSchema.nullable(),
        requiresKpiClass: z.boolean(),
        exactSafetyManager: z.boolean(),
      })
      .strict(),
    supportedInspectionCategories: z.tuple([
      z.literal('structured'),
      z.literal('special'),
      z.literal('quick'),
    ]),
    structuredKinds: z.tuple([
      z.literal('joint'),
      z.literal('weekly'),
      z.literal('patrol'),
      z.literal('night'),
    ]),
    areas: z.array(inspectionSetupAreaSchema),
    contractors: z.array(inspectionSetupContractorSchema),
    trades: z.array(inspectionSetupTradeSchema),
    categories: z.array(inspectionSetupCategorySchema),
    templates: z.array(inspectionSetupTemplateSchema),
    readiness: inspectionSetupReadinessSchema,
    missingAxes: z.array(inspectionSetupMissingAxisSchema),
    participants: z.array(inspectionSetupParticipantSchema),
    injuryTypes: z.array(z.unknown()),
    injuryTypeTaxonomy: nonEmptyTextSchema,
    duePolicy: inspectionSetupDuePolicySchema,
    complete: z.literal(true),
    source: z.literal('server-read'),
  })
  .strict();

export type InspectionSetupRequest = z.infer<typeof inspectionSetupRequestSchema>;
export type InspectionSetupOptions = z.infer<typeof inspectionSetupOptionsSchema>;
